import { Component, Input, OnInit } from '@angular/core';

import { IParty } from '@tablesready/common-ui';
import { AppIcon, AppIconColor } from '@tablesready/frontend-icons';
import { AuthUser } from '@shared/signup/models';
import {
  BillingVisibility,
  FrontendPaidFeatureService,
} from '@tablesready/frontend-paid-feature-alert';

import { NotifyActions } from '../waitlist-notify';

export interface WaitlistActionsCallbacks<T> {
  addEditParty: (party: T) => void;
  checkPartyResources: (party: T) => void;
  confirmDelete: (party: T) => void;
  chatParty: (party: T, index: number) => void;
  checkIn?: (party: T) => void; // optional: only the collapsed (waitlist) layout uses it
}

@Component({
  selector: 'tablesready-waitlist-actions',
  templateUrl: './waitlist-actions.component.html',
  styleUrls: ['./waitlist-actions.component.scss'],
  standalone: false,
})
export class WaitlistActionsComponent implements OnInit {
  @Input() togoMode = false;
  @Input() party?: IParty;
  @Input() index = 0;
  @Input() isMobile = false;
  @Input() callbacks?: WaitlistActionsCallbacks<IParty>;
  @Input() user?: AuthUser;
  @Input() notifyActions?: NotifyActions<IParty>;   // when present → collapsed layout
  @Input() voicePageNotificationEnabled = false;
  @Input() timezone?: string;
  @Input() anyPartyNotifiable = false;              // some row shows the Text button → reserve its slot here
  /**
   * Rendered as a card field in the narrow-screen list (not a table column). The group sits
   * at the card's left edge and is only as wide as its buttons, so the Text slot is always
   * reserved there — otherwise a card without the Text button shifts ⋮ and Serve left.
   */
  @Input() listLayout = false;

  chatBtnFocus: Record<number, boolean> = { };
  icon = AppIcon;
  color = AppIconColor;

  constructor(readonly paidService: FrontendPaidFeatureService) { }

  ngOnInit(): void { }

  chatTooltip(phoneNumber?: string): string {
    if (this.isMobile) {
      return '';
    }

    return !phoneNumber ? 'No phone provided' : 'Chat';
  }

  /**
   * The ⋮ overflow menu also carries Voice notify and Chat. When neither applies the only
   * item left would be Cancel, so callers surface a direct Cancel button instead of a
   * single-item dropdown.
   */
  get showOverflowMenu(): boolean {
    const showVoiceNotify =
      this.canNotifyReady
      && this.voicePageNotificationEnabled
      && !this.togoMode
      && !!this.notifyActions?.voicePage;

    return showVoiceNotify || this.showChatInMenuItem;
  }

  get showChatInMenuItem(): boolean {
    return this.paidService.billingsVisibility !== BillingVisibility.HIDDEN_ON_MOBILE;
  }

  get canNotifyReady(): boolean {
    return this.togoMode || !!this.party?.checkin_time;
  }

  /**
   * The Text button is shown only when it's actually usable — the party is checked in (or
   * togo) AND has a phone. When it can't be used we hide it rather than show a dead disabled
   * button (no tooltip to explain "why" on touch); its slot is reserved instead so the
   * Cancel/CTA columns stay aligned. A visible Text button is then only ever disabled for a
   * transient reason (readyNotificationDisabled → disableMessageButton).
   */
  get showTextButton(): boolean {
    return this.canNotifyReady && !!this.party?.phone;
  }

  get readyNotificationDisabled(): boolean {
    return !this.party?.phone || this.notifyActions?.disableMessageButton(this.party) === true;
  }

  voicePage(): void {
    if (!this.party || !this.notifyActions?.voicePage || this.readyNotificationDisabled) {
      return;
    }

    this.notifyActions.voicePage(this.party);
  }

  protected readonly BillingVisibility = BillingVisibility;
}
